import { venueStyle } from './style'
import {
    mapCameraAnimationDuration,
    mapDefaultZoom,
} from '@/features/orders/components/MapPanel/parts/Content/constants'
import {
    mapLayerIds,
    mapSourceIds,
} from '@/features/orders/components/MapPanel/parts/Content/parts/Map/constants/layerConfig'
import { pinIconOffset } from '@/features/orders/components/MapPanel/parts/Content/parts/Map/constants/pinStyle'
import { MapProps } from '@/features/orders/components/MapPanel/parts/Content/parts/Map/types'
import { VenueMapPoint } from '@/features/orders/components/MapPanel/parts/Content/types'
import { toMapFeatures } from '@/features/orders/components/MapPanel/parts/Content/utils/toMapFeatures'
import { useLatestValueRef } from '@/hooks/useLatestValueRef'
import type mapboxgl from 'mapbox-gl'
import { useEffect, useMemo, useRef, useState } from 'react'

interface UseVenueLayerOptions extends Pick<MapProps, 'paused' | 'onError'> {
    map: mapboxgl.Map | undefined
    venue: VenueMapPoint | undefined
}

export const useVenueLayer = ({ map, venue, paused, onError }: UseVenueLayerOptions) => {
    const latest = useLatestValueRef({ venue, onError })
    const shape = useMemo(() => toMapFeatures(venue ? [venue] : []), [venue])
    const applied = useRef<{ map: mapboxgl.Map; shape: typeof shape } | undefined>(undefined)
    const [readyMap, setReadyMap] = useState<mapboxgl.Map | undefined>(undefined)

    useEffect(() => {
        if (!map) return

        try {
            map.addSource(mapSourceIds.venue, {
                type: 'geojson',
                data: toMapFeatures([]),
            })
            map.addLayer({
                id: mapLayerIds.venuePin,
                type: 'symbol',
                source: mapSourceIds.venue,
                layout: {
                    'icon-image': venueStyle.iconImage,
                    'icon-anchor': venueStyle.iconAnchor,
                    'icon-offset': pinIconOffset,
                    'icon-allow-overlap': venueStyle.allowOverlap,
                },
            })
            map.on('click', mapLayerIds.venuePin, () => {
                const coordinate = latest.current.venue?.coordinate
                if (coordinate) {
                    map.easeTo({
                        center: coordinate,
                        zoom: mapDefaultZoom,
                        duration: mapCameraAnimationDuration,
                    })
                }
            })
            map.on('mouseenter', mapLayerIds.venuePin, () => {
                map.getCanvas().style.cursor = 'pointer'
            })
            map.on('mouseleave', mapLayerIds.venuePin, () => {
                map.getCanvas().style.cursor = ''
            })
            setReadyMap(map)
        } catch (error) {
            latest.current.onError(error)
        }
    }, [latest, map])

    useEffect(() => {
        if (!map || paused) return

        if (applied.current?.map === map && applied.current.shape === shape) return

        const source = map.getSource<mapboxgl.GeoJSONSource>(mapSourceIds.venue)
        if (!source) return

        source.setData(shape)
        applied.current = { map, shape }
    }, [map, paused, shape])

    return !!map && readyMap === map
}
